"use client";

import { createConsultation, updateConsultation } from "@/app/apis/auth/helper";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUIState } from "@/context/state-context";
import { format } from "date-fns";
import {
  Building,
  CalendarIcon,
  Clock,
  Mail,
  MessageSquare,
  Phone as PhoneIcon,
  User,
  Video,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import Error from "./shared/error";
import { toast } from "sonner";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface ScheduleCallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  reload?: () => void;
}

// Available time slots
const TIME_SLOTS = [
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
];

// Available durations
const DURATIONS = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "1 hour" },
];

export function ScheduleCallModal({
  open,
  onOpenChange,
  initialData,
  reload,
}: ScheduleCallModalProps) {
  // Step management
  const [step, setStep] = useState<
    "basic" | "details" | "calendar" | "confirmation"
  >("basic");
  // Basic information (Step 1)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  });

  // Meeting details (Step 2)
  const [meetingDetails, setMeetingDetails] = useState({
    topic: "",
    notes: "",
    meetingType: "video" as "video" | "call" | "in-person",
    status: "pending" as "confirmed" | "pending" | "declined" | "tentative",
    duration: 30,
    meetingLink: "",
    phoneNumber: "",
    participants: [] as string[],
  });

  const [newParticipant, setNewParticipant] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>("");
  const { error, setError } = useUIState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData && !open) {
      onOpenChange(true);
    }
  }, [initialData, onOpenChange]);

  // Populate form if initialData is provided (edit mode)
  useEffect(() => {
    if (!initialData) return;

    // Step 1: Form data
    setFormData({
      name: initialData.name ?? "",
      email: initialData.email ?? "",
      phone: initialData.phone ?? "",
      company: initialData.company ?? "",
    });

    // Step 2: Meeting details
    setMeetingDetails((prev) => ({
      ...prev,
      topic: initialData.topic ?? "",
      notes: initialData.notes ?? "",
      meetingType: initialData.meetingType ?? "video",
      status: initialData.status ?? "pending",
      duration: initialData.duration ?? 30,
      meetingLink: initialData.meetingLink ?? "",
      phoneNumber: initialData.phoneNumber ?? "",
      participants: initialData.participants
        ? initialData.participants.map((p: any) => p.email)
        : [],
    }));

    // Step 3: Date + Time (extracted from `date`)
    if (initialData.date) {
      const dateObj = new Date(initialData.date);

      setSelectedDate(dateObj);

      const hours = dateObj.getHours().toString().padStart(2, "0");
      const minutes = dateObj.getMinutes().toString().padStart(2, "0");

      setSelectedTime(`${hours}:${minutes}`);
    }
  }, [initialData]);

  // Handle meeting type changes
  useEffect(() => {
    setMeetingDetails((prev) => {
      if (prev.meetingType === "video") {
        return {
          ...prev,
          phoneNumber: "", // remove phone
        };
      }

      if (prev.meetingType === "call") {
        return {
          ...prev,
          meetingLink: "", // remove link
        };
      }

      // in-person
      return {
        ...prev,
        meetingLink: "",
        phoneNumber: "",
      };
    });
  }, [meetingDetails.meetingType]);

  const handleAddParticipant = () => {
    if (
      newParticipant.trim() &&
      !meetingDetails.participants.includes(newParticipant.trim())
    ) {
      setMeetingDetails({
        ...meetingDetails,
        participants: [...meetingDetails.participants, newParticipant.trim()],
      });
      setNewParticipant("");
    }
  };

  const handleRemoveParticipant = (index: number) => {
    const updatedParticipants = [...meetingDetails.participants];
    updatedParticipants.splice(index, 1);
    setMeetingDetails({
      ...meetingDetails,
      participants: updatedParticipants,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Combine date and time
      if (selectedDate && selectedTime) {
        const [hours, minutes] = selectedTime.split(":");
        const scheduledDate = new Date(selectedDate);
        scheduledDate.setHours(parseInt(hours), parseInt(minutes));

        const payload = {
          ...formData,
          ...meetingDetails,
          date: scheduledDate.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        } as any;

        const response = initialData
          ? await updateConsultation(initialData.id, payload) // EDIT MODE
          : await createConsultation(payload);

        if (!response.success) {
          setError(response.message);
          return;
        }

        reload?.();

        toast.success(response.message);

        setStep("confirmation");
      }
    } catch (error) {
      console.error("Error scheduling call:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
  };

  if (error) {
    return <Error errorMessage={error} reload={() => setError("")} />;
  }

  const renderStep = () => {
    switch (step) {
      case "basic":
        return (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setStep("calendar");
            }}
            className="space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-900"
                >
                  Full Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="name"
                    placeholder="John Doe"
                    className="pl-10 border-gray-300 focus:border-cyan-600 focus:ring-cyan-600"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-900"
                >
                  Email Address *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    className="pl-10 border-gray-300 focus:border-cyan-600 focus:ring-cyan-600"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label
                  htmlFor="phone"
                  className="text-sm font-medium text-gray-900"
                >
                  Phone Number
                </Label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    className="pl-10 border-gray-300 focus:border-cyan-600 focus:ring-cyan-600"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label
                  htmlFor="company"
                  className="text-sm font-medium text-gray-900"
                >
                  Company
                </Label>
                <div className="relative">
                  <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="company"
                    placeholder="Acme Inc."
                    className="pl-10 border-gray-300 focus:border-cyan-600 focus:ring-cyan-600"
                    value={formData.company}
                    onChange={(e) =>
                      setFormData({ ...formData, company: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1 border-gray-300 hover:border-cyan-600 hover:text-cyan-600"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                disabled={!formData.name || !formData.email}
              >
                Next: Choose Date & Time
              </Button>
            </div>
          </form>
        );

      case "calendar":
        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-cyan-100 flex items-center justify-center text-cyan-600 ">
                  <CalendarIcon className="w-4 h-4" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Select Date & Time
                </h3>
              </div>
              <div className="border border-gray-200  p-4">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  className=""
                  disabled={(date) => {
                    // Disable past dates
                    return (
                      date < new Date(new Date().setHours(0, 0, 0, 0)) ||
                      // Disable weekends
                      date.getDay() === 0 ||
                      date.getDay() === 6
                    );
                  }}
                />
              </div>
            </div>

            {selectedDate && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-cyan-100 flex items-center justify-center text-cyan-600 ">
                    <Clock className="w-4 h-4" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Select Time
                  </h3>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {TIME_SLOTS.map((time) => (
                    <Button
                      key={time}
                      type="button"
                      variant={selectedTime === time ? "default" : "outline"}
                      className={`h-12 ${selectedTime === time ? "bg-cyan-600 hover:bg-cyan-700 text-white" : "border-gray-300 hover:border-cyan-600 hover:text-cyan-600"}`}
                      onClick={() => setSelectedTime(time)}
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      {time}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep("basic")}
                className="flex-1 border-gray-300 hover:border-cyan-600 hover:text-cyan-600"
              >
                ← Back
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (selectedDate && selectedTime) {
                    // Scroll the dialog content to top
                    const dialogContent = document.querySelector(
                      "[data-dialog-content]",
                    );
                    if (dialogContent) {
                      dialogContent.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }
                    setStep("details");
                  }
                }}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                disabled={!selectedDate || !selectedTime}
              >
                Next: Meeting Details
              </Button>
            </div>
          </div>
        );

      case "details":
        return (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step indicator */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-cyan-100  flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-cyan-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Meeting Details
                </h3>
              </div>
              <div className="text-sm text-gray-500">Step 3 of 3</div>
            </div>

            <div className="space-y-3">
              <Label
                htmlFor="topic"
                className="text-sm font-medium text-gray-900"
              >
                Meeting Topic *
              </Label>
              <Input
                id="topic"
                placeholder="Project discussion, Consultation, etc."
                className="border-gray-300 focus:border-cyan-600 focus:ring-cyan-600"
                required
                value={meetingDetails.topic}
                onChange={(e) =>
                  setMeetingDetails({
                    ...meetingDetails,
                    topic: e.target.value,
                  })
                }
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="block text-sm font-medium text-gray-900">
                  Duration (minutes)
                </Label>
                <Select
                  value={meetingDetails.duration.toString()}
                  onValueChange={(v) =>
                    setMeetingDetails({
                      ...meetingDetails,
                      duration: parseInt(v),
                    })
                  }
                >
                  <SelectTrigger className="w-full border-gray-300 focus:border-cyan-500 focus:ring-cyan-500">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map((duration) => (
                      <SelectItem
                        key={duration.value}
                        value={duration.value.toString()}
                      >
                        {duration.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="block text-sm font-medium text-gray-900">
                  Meeting Type
                </Label>
                <Select
                  value={meetingDetails.meetingType}
                  onValueChange={(v: "video" | "call" | "in-person") =>
                    setMeetingDetails({ ...meetingDetails, meetingType: v })
                  }
                >
                  <SelectTrigger className="w-full border-gray-300 focus:border-cyan-500 focus:ring-cyan-500">
                    <SelectValue placeholder="Select meeting type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video Call</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="in-person">In-person</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="block text-sm font-medium text-gray-900">
                  Status
                </Label>
                <Select
                  value={meetingDetails.status}
                  onValueChange={(v: "confirmed" | "pending" | "tentative") =>
                    setMeetingDetails({ ...meetingDetails, status: v })
                  }
                  disabled
                >
                  <SelectTrigger className="w-full border-gray-300 focus:border-cyan-500 focus:ring-cyan-500">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="tentative">Tentative</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {meetingDetails.meetingType === "video" && (
              <div className="space-y-2">
                <Label className="block text-sm font-medium text-gray-900">
                  Meeting Link
                </Label>
                <Input
                  type="url"
                  value={meetingDetails.meetingLink}
                  onChange={(e) =>
                    setMeetingDetails({
                      ...meetingDetails,
                      meetingLink: e.target.value,
                    })
                  }
                  className="w-full border-gray-300 focus:border-cyan-500 focus:ring-cyan-500"
                  placeholder="https://meet.google.com/..."
                />
              </div>
            )}

            {meetingDetails.meetingType === "call" && (
              <div className="space-y-2">
                <Label className="block text-sm font-medium text-gray-900">
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  value={meetingDetails.phoneNumber}
                  onChange={(e) =>
                    setMeetingDetails({
                      ...meetingDetails,
                      phoneNumber: e.target.value,
                    })
                  }
                  className="w-full border-gray-300 focus:border-cyan-500 focus:ring-cyan-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            )}

            {/* Participants Section */}
            <div className="space-y-3">
              <Label className="block text-sm font-medium text-gray-900">
                Participants
              </Label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newParticipant}
                    onChange={(e) => setNewParticipant(e.target.value)}
                    className="flex-1 border-gray-300 focus:border-cyan-500 focus:ring-cyan-500"
                    placeholder="Enter participant email or name"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddParticipant();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={handleAddParticipant}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    disabled={!newParticipant.trim()}
                  >
                    Add
                  </Button>
                </div>

                {meetingDetails.participants.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {meetingDetails.participants.length} participant
                      {meetingDetails.participants.length !== 1 ? "s" : ""}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {meetingDetails.participants.map((participant, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5  text-sm"
                        >
                          <span>{participant}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveParticipant(index)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label
                htmlFor="notes"
                className="text-sm font-medium text-gray-900"
              >
                Additional Notes
              </Label>
              <Textarea
                id="notes"
                placeholder="Any specific topics you'd like to discuss..."
                rows={4}
                className="border-gray-300 focus:border-cyan-600 focus:ring-cyan-600"
                value={meetingDetails.notes}
                onChange={(e) =>
                  setMeetingDetails({
                    ...meetingDetails,
                    notes: e.target.value,
                  })
                }
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep("calendar")}
                className="flex-1 border-gray-300 hover:border-cyan-600 hover:text-cyan-600"
              >
                ← Back
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                disabled={!meetingDetails.topic || loading}
              >
                {loading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin  border-2 border-current border-t-transparent" />
                    Scheduling...
                  </>
                ) : (
                  "Schedule Call"
                )}
              </Button>
            </div>
          </form>
        );

      case "confirmation":
        return (
          <div className="text-center py-2">
            <div className="w-20 h-20 bg-cyan-100  flex items-center justify-center mx-auto mb-6">
              <CalendarIcon className="w-10 h-10 text-cyan-600" />
            </div>

            <div className="mb-8">
              <p className="text-cyan-600 text-xs uppercase font-medium mb-2">
                Successfully Scheduled
              </p>
              <h3 className="text-2xl font-bold mb-3">
                Call <span className="text-cyan-600">Scheduled!</span>
              </h3>
            </div>

            <div className="bg-gray-50 border border-gray-200  p-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-600 flex items-center justify-center text-white ">
                  <User className="w-6 h-6" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-semibold text-gray-900">
                    {formData.name}
                  </h4>
                  {formData.company && (
                    <p className="text-sm text-gray-600 mb-2">
                      {formData.company}
                    </p>
                  )}
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <span>
                        {selectedDate &&
                          format(selectedDate, "EEEE, MMMM d, yyyy")}{" "}
                        at {selectedTime}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span>Duration: {meetingDetails.duration} minutes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {meetingDetails.meetingType === "video" ? (
                        <Video className="w-4 h-4 text-gray-400" />
                      ) : (
                        <PhoneIcon className="w-4 h-4 text-gray-400" />
                      )}
                      <span>
                        Type:{" "}
                        {meetingDetails.meetingType === "video"
                          ? "Video Call"
                          : meetingDetails.meetingType === "call"
                            ? "Phone Call"
                            : "In-person"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <span>
                        Status:{" "}
                        {meetingDetails.status.charAt(0).toUpperCase() +
                          meetingDetails.status.slice(1)}
                      </span>
                    </div>
                    {meetingDetails.topic && (
                      <div className="flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                        <span>Topic: {meetingDetails.topic}</span>
                      </div>
                    )}
                    {meetingDetails.participants.length > 0 && (
                      <div className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <span>Participants: </span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {meetingDetails.participants.map(
                              (participant, index) => (
                                <span
                                  key={index}
                                  className="text-xs bg-gray-100 px-2 py-1 "
                                >
                                  {participant}
                                </span>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  // Reset form
                  setStep("basic");
                  setSelectedDate(undefined);
                  setSelectedTime("");
                  setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    company: "",
                  });
                  setMeetingDetails({
                    topic: "",
                    notes: "",
                    meetingType: "video",
                    status: "pending",
                    duration: 30,
                    meetingLink: "",
                    phoneNumber: "",
                    participants: [],
                  });
                  setNewParticipant("");
                }}
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                Done
              </Button>
              <p className="text-sm text-gray-500">
                Need to make changes? Contact us at info@hexad.co.rw
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[98%] overflow-y-auto border-gray-200">
        {step !== "confirmation" ? (
          <DialogHeader className="mb-6">
            <div className="mb-2">
              <p className="text-cyan-600 text-xs uppercase font-medium">
                Schedule Consultation
              </p>

              <DialogTitle className="text-2xl font-bold mt-2">
                Schedule a <span className="text-cyan-600">Call</span>
              </DialogTitle>
            </div>

            <DialogDescription className="text-gray-600 text-base">
              Book a time that works for you. We'll send you a confirmation with
              meeting details.
            </DialogDescription>
          </DialogHeader>
        ) : (
          <VisuallyHidden>
            <DialogTitle>Consultation Confirmation</DialogTitle>
          </VisuallyHidden>
        )}

        {renderStep()}
      </DialogContent>
    </Dialog>
  );
}
